import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import {
  AbstractControl,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { FEATURE_TOGGLER, FeatureToggler } from '@cbre/feature-toggles';
import { Observable, of, throwError } from 'rxjs';
import { catchError, filter, switchMap } from 'rxjs/operators';
import { IUser } from '../../../models';
import { FloorClass } from '../../../models/floor.class';
import { NonRentableSpaceClass } from '../../../models/space/non-rentable-space.class';
import { COUNTRY_MAP } from '../../../shared/constants/countries.const';
import { SHOW_FLOOR_INDEX_FEATURE_KEY } from '../../../shared/constants/features.const';
import { FloorContainerRepository } from '../../floor-container/floor-container-repository.service';
import { isNotNullOrEmptyString } from '../../../shared/utils/functions';
import { StackingPlanService } from '../../../stacking-plan.service';
import { ActionsToolbarService } from '../../actions-toolbar/actions-toolbar.service';
import { EDPSpaceInfo } from '../../floor-container/floor/item/item.model';
import { FloorOption } from '../more-info-dialog/elevators/elevators.model';
import { deleteNonRentableSpaceInfo } from './non-rentable-space.functions';
import { NonRentableData, ReqDeleteNonRentableSpace, ReqUpdateNonRentableSpace } from './non-rentable-space.model';
import { LovValues } from '../../../stacking-plan.model';
import { CbreLogger, CbreLoggerToken } from '@cbre/logger';

@Component({
  selector: 'sp-non-rentable-space',
  templateUrl: './non-rentable-space.component.html',
  styleUrls: ['./non-rentable-space.component.scss'],
  standalone: false,
})
export class NonRentableSpaceComponent implements OnInit, OnDestroy {
  nonRentableSpaceFG: UntypedFormGroup;
  spaceType: LovValues[] = [];
  floorsOptions: FloorOption[] = [];
  uomValues = [];
  floorData: FloorClass[];
  isReplacingTenant: boolean;

  private isUnaccounted: boolean;
  private readonly currentFloorNumber: string;
  public isIndiaUsr: boolean;
  private user: IUser;
  public spaceValidatnMsg = false;
  public spaceSizeValue: number;

  constructor(
    private fb: UntypedFormBuilder,
    public dialogRef: MatDialogRef<NonRentableSpaceComponent>,
    private stackingPlanService: StackingPlanService,
    private actionsToolbarService: ActionsToolbarService,
    @Inject(FEATURE_TOGGLER) private toggleRouterService: FeatureToggler,
    @Inject(MAT_DIALOG_DATA) private data: NonRentableData,
    private floorsRepository: FloorContainerRepository,
    @Inject(CbreLoggerToken) private readonly logger: CbreLogger
  ) {
    // store existing floorNo. used when updating floor number
    const { floorNo, isReplacingTenant = false, isUnaccounted = false } = data;
    this.currentFloorNumber = floorNo;
    this.isReplacingTenant = isReplacingTenant;
    this.isUnaccounted = isUnaccounted;
  }

  ngOnInit() {
    this.getFloors();
    this.getSpaceNames();
    this.getUOMLOVs();

    this.user = this.stackingPlanService.resolveUser();
    this.isIndiaUsr = COUNTRY_MAP.India.includes(this.user.userCountry);
    let suiteNumberVal = '';
    let spaceSizeVal = null;
    let spaceSizeUOMVal = null;
    if (this.data.item != null) {
      suiteNumberVal = this.data.item.suiteNumber;
      spaceSizeVal = this.data.item.nonRentableSize;
      spaceSizeUOMVal = this.data.item.nonRentableSizeUomDesc;
      if (this.data.isUnaccounted) {
        suiteNumberVal = this.data.item.suiteNumber;
        spaceSizeVal = this.data.item['unaccountedSpace'];
        spaceSizeUOMVal = this.data.item['unaccountedSpaceUOM'];
      }
    } else if (this.data.suiteNumber != null) {
      suiteNumberVal = this.data.suiteNumber;
      spaceSizeVal = this.data.spaceSize;
      spaceSizeUOMVal = this.data.spaceSizeUOM;
    }
    this.spaceSizeValue = spaceSizeVal;
    this.nonRentableSpaceFG = this.fb.group({
      spaceName: new UntypedFormControl(
        this.data.item ? this.data.item.spaceClassificationTypeDesc : '',
        Validators.required
      ),
      floorNumber: new UntypedFormControl(
        { value: this.data.floorNo, disabled: this.isReplacingTenant },
        Validators.required
      ),
      suiteNo: new UntypedFormControl(suiteNumberVal),
      spaceSize: new UntypedFormControl(spaceSizeVal, { updateOn: 'blur' }),
      spaceSizeUOM: new UntypedFormControl(spaceSizeUOMVal),
      note: new UntypedFormControl(this.data.item ? this.data.item.notes : ''),
    });
    this.nonRentableSpaceFG.controls.spaceSize.valueChanges
      .pipe(filter(spaceSizeValue => spaceSizeValue != null))
      .subscribe(() => this.onSpaceSizeUpdate());

    const validations = this.chkSpaceSizeValidation();
    this.nonRentableSpaceFG.setValidators(validations);
  }

  ngOnDestroy(): void {
    /* required by angular-l10n */
  }

  submit() {
    const currentItem = this.data.item;
    const {
      floorNumber,
      suiteNo,
      spaceName,
      spaceSize,
      spaceSizeUOM,
      note: notes,
    } = this.nonRentableSpaceFG.getRawValue();
    const floorToPush = this.floorData.find(floor => floor.floorNumber === floorNumber);
    const query = new ReqUpdateNonRentableSpace(this.stackingPlanService.propertyId, {
      floorId: floorToPush != null ? floorToPush.floorId : null,
      floorNumber,
      suiteNo,
      spaceName,
      spaceSize,
      spaceSizeUOM,
      notes,
      suiteId: currentItem != null ? currentItem.suiteId : null,
    });

    this.stackingPlanService
      .alertNonTrustedUser()
      .pipe(filter(Boolean))
      .subscribe(() => {
        const requestToCreateNRS$ = this.updateNonRentableSpace(query);
        if (this.isReplacingTenant && !this.isUnaccounted) {
          this.dialogRef.close({ requestToCreateNRS$, finalSpaceSize: spaceSize, finalSpaceSizeUOM: spaceSizeUOM });
        } else {
          requestToCreateNRS$
            .pipe(this.actionsToolbarService.errorHandling('errorMessages.updateNonRentableSpace'))
            .subscribe(error => {
              let toReturnOnClose;
              if (error == null || typeof error !== 'string') {
                const edpSpaceInfo: EDPSpaceInfo = {
                  suite_id: currentItem != null ? currentItem.suiteId : null,
                  suite_number: suiteNo,
                  suite_desc: null,
                  occupancy_status: null,
                  availability_status: null,
                  tenant: null,
                  lease: null,
                  availability: null,
                  non_rentable_space: {
                    space_classification_type_desc: spaceName,
                    non_rentable_size: spaceSize,
                    non_rentable_size_uom_desc: spaceSizeUOM,
                    notes,
                  },
                };
                toReturnOnClose = { floorNumber, nonRentableSpace: new NonRentableSpaceClass(edpSpaceInfo) };
              }
              this.dialogRef.close(toReturnOnClose);
            });
        }
      });
  }

  private updateNonRentableSpace(reqUpdateNonRentableSpace: ReqUpdateNonRentableSpace): Observable<any> {
    const isUpdate = this.currentFloorNumber != null && !this.isReplacingTenant && !this.isUnaccounted;
    const deleteObservable = isUpdate ? this.deleteNonRentableSpace(reqUpdateNonRentableSpace) : of('');
    return deleteObservable.pipe(switchMap(() => this.patchNonRentableSpaceOnEDP(reqUpdateNonRentableSpace, isUpdate)));
  }

  private deleteNonRentableSpace({ property_id, suiteId: suite_id }: ReqUpdateNonRentableSpace): Observable<any> {
    const deleteNRSpace: ReqDeleteNonRentableSpace = deleteNonRentableSpaceInfo(
      this.floorData,
      property_id,
      suite_id,
      this.currentFloorNumber
    );
    return this.actionsToolbarService.deleteNonRentableData(deleteNRSpace).pipe(
      catchError(err => {
        this.logger.error(`Error deleting a non rentable space: ${err}`);
        return throwError(err);
      })
    );
  }

  private patchNonRentableSpaceOnEDP(reqNRSpace: ReqUpdateNonRentableSpace, isUpdate?: boolean): Observable<any> {
    if (isUpdate) {
      reqNRSpace.deleteSuiteId();
    }
    return this.actionsToolbarService.saveNonRentableData(reqNRSpace).pipe(
      catchError(err => {
        this.logger.error(`Error updating/creating a non rentable space: ${err}`);
        return throwError(err);
      })
    );
  }

  private chkSpaceSizeValidation(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } => {
      const floorNum = control.get('floorNumber').value;
      const spaceSize = control.get('spaceSize').value;
      this.spaceValidatnMsg = false;

      if (floorNum != null && spaceSize) {
        const floor = this.getFloorInfo(floorNum);
        if ((floor && !floor.floorArea) || !floor) {
          return;
        }
        const remainingSpace = floor.floorArea - floor.occupiedArea;
        if (spaceSize > remainingSpace) {
          this.spaceValidatnMsg = true;
        }
        return spaceSize > remainingSpace && this.isIndiaUsr ? { spaceValidatnMsg: true } : null;
      }
    };
  }

  private getFloorInfo(floorNumber): FloorClass {
    return this.floorData?.find(ele => {
      const floorNum = ele.floorNumber ?? String(ele.floorIndex);
      return floorNum === floorNumber;
    });
  }

  private onSpaceSizeUpdate() {
    const spaceSizeUOMFC = this.nonRentableSpaceFG.controls.spaceSizeUOM as UntypedFormControl;
    const currentUOMValue = spaceSizeUOMFC.value;
    if (!isNotNullOrEmptyString(currentUOMValue) && this.uomValues.length > 0) {
      spaceSizeUOMFC.setValue(this.uomValues[0].value);
    }
  }

  private getSpaceNames() {
    this.actionsToolbarService.getSpaceNameLOVs().subscribe(data => {
      if (data) {
        this.spaceType = data;
      }
    });
  }

  private getFloors() {
    this.floorData = this.floorsRepository.floorData;
    this.floorsOptions = FloorClass.generateFloorCatalogOptionsFrom(this.floorData, {
      disableIfFloorNumberValue: false,
      showIndex: this.toggleRouterService.isFeatureEnabled(SHOW_FLOOR_INDEX_FEATURE_KEY),
      showHiddenFloors: false,
      showInvalidFloors: true,
    });
  }

  private getUOMLOVs() {
    this.actionsToolbarService.getAreaLOVs().subscribe(data => {
      if (data) {
        this.uomValues = data;
      }
    });
  }
}
